/**
 * RestoID — third-party consent (authorize an application).
 * ---------------------------------------------------------------------------
 * RestoID PRESENTATION around the PRISTINE upstream Consent logic. Every hook,
 * guard, effect, error branch and navigation in `src/pages/Consent/index.tsx` is
 * copied here line for line — `useApi(getConsentInfo)` / `useApi(consent)`, the
 * `oidc.access_denied` error handler, the sign-out URL builder, the default
 * organization selection, the reserved-organization filter on resource scopes,
 * the redirect-uri cancel and the `redirectTo` hand-off. The upstream
 * `getRedirectUriOrigin` / `getClientIdentifierHost` helpers are IMPORTED rather
 * than re-implemented, so the URL handling stays one copy.
 *
 * Only the markup is ours. It replaces `@/Layout/LandingPageLayout` (which is
 * `FirstScreenLayout` + `BrandingHeader`) and the four presentation components
 * under `src/pages/Consent/` — see `custom/components/Consent*.tsx`, each of
 * which documents what it replaces and why.
 *
 * Deliberate differences from upstream, all of them copy or shape:
 *   - the layout is `FirstScreenLayout` directly, with the page meta title and
 *     interpolation LandingPageLayout would have passed
 *     (`description.authorize_title` + `{ name }`), so the browser tab is
 *     unchanged. The headline moves into the RestoID app lockup;
 *   - the scopes list is introduced by `description.request_permission`
 *     ("{{name}} запрашивает доступ к:") instead of
 *     `description.authorize_personal_data_usage`; the design asks for one short
 *     request line under the app name, and `request_permission` is the phrase
 *     Logto already has for exactly that sentence;
 *   - the decision buttons read "Разрешить" / "Отклонить" (`consentAuthorize` /
 *     `consentDeny`) rather than `action.authorize` / `action.cancel`. This is
 *     the design source's copy, and "Отменить" is wrong for the deny half of a
 *     consent decision — it names the dialog, not the choice. The terms sentence,
 *     the redirect line, the scope descriptions and the "not you?" link all stay
 *     on their Logto phrase keys, so Custom Phrases keep working;
 *   - the terms line keeps upstream's `description.authorize_agreement` /
 *     `..._with_redirect` (NOT `description.auto_agreement`): those are the two
 *     the Consent page renders, and they carry the application name and the
 *     redirect origin, which `auto_agreement` drops.
 *
 * Swapped in for `./pages/Consent` via `src/App.tsx`.
 */
import { ReservedResource } from '@logto/core-kit';
import { type ConsentInfoResponse, isCimdClientId } from '@logto/schemas';
import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import { consent, getConsentInfo } from '@/apis/consent';
import TermsLinks from '@/components/TermsLinks';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import { getClientIdentifierHost, getRedirectUriOrigin } from '@/pages/Consent/util';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { searchKeys } from '@/shared/utils/search-parameters';

import { ConsentActions } from '../components/ConsentActions';
import {
  ConsentAppHeader,
  ConsentUnregisteredNotice,
  ConsentUserRow,
} from '../components/ConsentHeader';
import { ConsentOrganization, type Organization } from '../components/ConsentOrganization';
import { ConsentScopeList } from '../components/ConsentScopes';
import { C, FONT, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

/**
 * Resolve how the client identifies itself on the page. (Upstream, verbatim.)
 *
 * An unregistered (CIMD) client is recognized by its identifier alone: the consent info keeps the
 * client identifier URL in `id`, and a registered application id never takes that shape. Its name
 * comes from the metadata document, which may omit `client_name` — the identifier host stands in
 * then, and stays on the page as a permanent identity signal either way.
 */
const getClientDisplayData = ({ application: { id, displayName, name } }: ConsentInfoResponse) => {
  const unregisteredClientHost = isCimdClientId(id) ? getClientIdentifierHost(id) : undefined;

  return {
    unregisteredClientHost,
    applicationName: (displayName ?? name) || (unregisteredClientHost ?? name),
  };
};

/** The small centred footnote under the buttons (terms / redirect notice). */
const Footnote = ({ children }: { children: ReactNode }) => (
  <p style={{ margin: 0, textAlign: 'center', color: C.faint, font: `400 12.5px/1.6 ${FONT}` }}>
    {children}
  </p>
);

const Consent = () => {
  const handleError = useErrorHandler();
  const asyncConsent = useApi(consent);
  const { t } = useTranslation();
  const c = useCopy();
  const redirectTo = useGlobalRedirectTo();
  const { experienceSettings, theme } = useContext(PageContext);

  const [consentData, setConsentData] = useState<ConsentInfoResponse>();
  const [selectedOrganization, setSelectedOrganization] = useState<Organization>();
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  const [isConsentLoading, setIsConsentLoading] = useState(false);

  const asyncGetConsentInfo = useApi(getConsentInfo);

  const consentErrorHandlers: ErrorHandlers = useMemo(
    () => ({
      'oidc.access_denied': () => {
        setIsAccessDenied(true);
      },
    }),
    []
  );

  const handleConsentError = useCallback(
    async (error: unknown) => {
      await handleError(error, consentErrorHandlers);
    },
    [consentErrorHandlers, handleError]
  );

  const signOut = useCallback(() => {
    const applicationId =
      new URLSearchParams(window.location.search).get(searchKeys.appId) ??
      consentData?.application.id;
    const signOutUrl = new URL('/oidc/session/end', window.location.origin);

    if (applicationId) {
      signOutUrl.searchParams.set('client_id', applicationId);
    }

    window.location.assign(signOutUrl.href);
  }, [consentData?.application.id]);

  const consentHandler = useCallback(async () => {
    setIsConsentLoading(true);
    const [error, result] = await asyncConsent(selectedOrganization?.id);
    setIsConsentLoading(false);

    if (error) {
      await handleConsentError(error);

      return;
    }

    if (result?.redirectTo) {
      await redirectTo(result.redirectTo);
    }
  }, [asyncConsent, handleConsentError, redirectTo, selectedOrganization?.id]);

  useEffect(() => {
    const getConsentInfoHandler = async () => {
      const [error, result] = await asyncGetConsentInfo();

      if (error) {
        await handleConsentError(error);

        return;
      }

      setConsentData(result);

      // Init the default organization selection
      if (!result?.organizations?.length) {
        return;
      }

      setSelectedOrganization(result.organizations[0]);
    };

    void getConsentInfoHandler();
  }, [asyncGetConsentInfo, handleConsentError]);

  if (isAccessDenied) {
    return (
      <ErrorPage
        isNavbarHidden
        title="error.access_denied"
        message="error.application_access_denied"
        primaryAction={{
          title: 'account_center.sessions.revoke_session',
          onClick: signOut,
        }}
      />
    );
  }

  if (!consentData) {
    return null;
  }

  const {
    application: { termsOfUseUrl, privacyPolicyUrl, branding },
  } = consentData;

  const { unregisteredClientHost, applicationName } = getClientDisplayData(consentData);
  const showTerms = Boolean(termsOfUseUrl ?? privacyPolicyUrl);
  const { redirectUri } = consentData;
  const redirectUriOrigin = consentData.redirectUri
    ? getRedirectUriOrigin(consentData.redirectUri)
    : undefined;

  /**
   * The client's own logo, resolved exactly as LandingPageLayout resolved the
   * `thirdPartyLogo` it handed to BrandingHeader. `experienceSettings` can still
   * be missing here (upstream's layout rendered nothing at all in that case);
   * without it the tenant has no dark-mode variant to choose from, which is what
   * `isDarkModeEnabled: false` already means to this helper.
   */
  const appLogoUrl = branding
    ? getBrandingLogoUrl({
        theme,
        branding,
        isDarkModeEnabled: experienceSettings?.color.isDarkModeEnabled ?? false,
      })
    : undefined;

  return (
    <FirstScreenLayout
      pageMeta={{
        titleKey: 'description.authorize_title',
        titleKeyInterpolation: { name: applicationName },
      }}
    >
      <Stack gap={18}>
        <ConsentAppHeader
          logoUrl={appLogoUrl}
          title={t('description.authorize_title', { name: applicationName })}
        />
        {unregisteredClientHost ? (
          <ConsentUnregisteredNotice host={unregisteredClientHost} />
        ) : null}
        <ConsentUserRow user={consentData.user} />
        <ConsentScopeList
          userScopes={consentData.missingOIDCScope}
          /**
           * The org resources is included in the user scopes for compatibility.
           */
          resourceScopes={consentData.missingResourceScopes?.filter(
            ({ resource }) => resource.id !== ReservedResource.Organization
          )}
          appName={applicationName}
        />
        {consentData.organizations ? (
          <ConsentOrganization
            organizations={consentData.organizations}
            selectedOrganization={selectedOrganization}
            onSelect={setSelectedOrganization}
          />
        ) : null}
        <ConsentActions
          denyLabel={c('consentDeny')}
          onDeny={
            redirectUri
              ? () => {
                  window.location.replace(redirectUri);
                }
              : undefined
          }
          authorizeLabel={c('consentAuthorize')}
          isAuthorizing={isConsentLoading}
          onAuthorize={() => {
            void consentHandler();
          }}
        />
        {!showTerms && redirectUriOrigin ? (
          <Footnote>{t('description.redirect_to', { name: redirectUriOrigin })}</Footnote>
        ) : null}
        {showTerms && redirectUriOrigin ? (
          <Footnote>
            <Trans
              components={{
                link: (
                  <TermsLinks
                    inline
                    termsOfUseUrl={termsOfUseUrl ?? ''}
                    privacyPolicyUrl={privacyPolicyUrl ?? ''}
                  />
                ),
              }}
            >
              {t('description.authorize_agreement_with_redirect', {
                name: applicationName,
                uri: redirectUriOrigin,
              })}
            </Trans>
          </Footnote>
        ) : null}
        {showTerms && !redirectUriOrigin ? (
          <Footnote>
            <Trans
              components={{
                link: (
                  <TermsLinks
                    inline
                    termsOfUseUrl={termsOfUseUrl ?? ''}
                    privacyPolicyUrl={privacyPolicyUrl ?? ''}
                  />
                ),
              }}
            >
              {t('description.authorize_agreement', {
                name: applicationName,
              })}
            </Trans>
          </Footnote>
        ) : null}
        <p style={{ margin: 0, textAlign: 'center', color: C.sub, font: `400 14px/1.5 ${FONT}` }}>
          {t('description.not_you')}{' '}
          <TextLink replace to="/sign-in" text="action.use_another_account" />
        </p>
      </Stack>
    </FirstScreenLayout>
  );
};

export default Consent;
